<template>
  <div v-show="hasContent && headings.length" class="in-page-navigation">
    <div class="in-page-navigation__inner">
      <nav aria-label="In-page navigation" role="contentinfo">
        <div class="heading">
          <button
            :class="{ active: shouldShow }"
            class="trigger flex align-center justify-between"
            type="button"
            @click="toggleNav"
          >
            <span>
              <strong> On this page </strong>
            </span>
            <i class="icon icon--opens visible-xs">
              <Icon name="fa6-solid:angle-down" />
            </i>
          </button>
        </div>
        <ul :class="{ active: shouldShow }">
          <li v-for="heading in headings" :key="heading.anchor">
            <a
              v-scroll-to="heading.anchor"
              :class="{ active: activeHeading === heading.anchor }"
              :href="heading.anchor"
              class="flex align-center justify-between"
              @click="closeNav"
            >
              <span>
                {{ heading.title }}
              </span>
              <i class="icon">
                <Icon name="fa6-solid:angle-right" />
              </i>
            </a>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { RichtextStoryblok } from '~/types/storyblok'

const props = defineProps<{
  content?: RichtextStoryblok
  title?: string
  introduction?: string
}>()
const route = useRoute()
const shouldShow = ref(false)
const content = computed(() => props.content?.content || [])
const hasContent = computed(() => !!content.value.length)
const headings = computed(() => {
  if (!hasContent.value) return []

  return content.value
    .filter((item) => {
      return (
        (item.type === 'heading' &&
          item?.attrs?.level === 2 &&
          item?.content?.[0]?.text) ||
        (item.type === 'blok' &&
          item.attrs?.body?.[0]?.icon?.filename &&
          item.attrs?.body?.[0]?.title &&
          item.attrs?.body?.[0]?.type === 'h2')
      )
    })
    .map((item) => {
      const text = item.content
        ? item.content[0].text
        : item.attrs.body[0].title

      return {
        title: text,
        anchor: `#${slugifyToAttribute(text)}`,
      }
    })
})

const activeHeading = computed(
  () => (clientMounted.value && route.hash) || undefined,
)

function toggleNav() {
  shouldShow.value = !shouldShow.value
}

function closeNav() {
  shouldShow.value = false
}

const clientMounted = ref(false)
onMounted(() => {
  if (import.meta.client) {
    // avoid hydration warning by deferring active hash check
    nextTick(() => (clientMounted.value = true))
  }
})
</script>

<style lang="less">
.page.featured-topics.powers-of-attorney {
  .in-page-navigation .in-page-navigation__inner nav > .heading {
    padding: @8px @gutter-sm;

    .trigger {
      background: @color-secondary;
      color: @color-fill-normal;
      height: @40px;
      padding: 0 @20px;

      @media @tablet {
        background: transparent;
        color: inherit;
        height: @sticky-nav-height;
        padding-left: 0;
      }

      .icon--opens {
        color: @color-fill-normal;
      }
    }
  }
}
</style>

<style lang="less">
.page-intro .in-page-navigation {
  display: none;

  .page.featured-topics.powers-of-attorney & {
    display: block;

    &.is-stuck {
      position: fixed;
      top: @mobile-nav-height;
      left: @gutter;
      width: 100%;
      z-index: 10;
    }

    @media @tablet {
      display: none;
    }
  }
}

.page.featured-topics.powers-of-attorney {
  .page-banner.with-page-navigation {
    margin-top: 0;
  }

  .sticky-sidebar {
    display: none;

    @media @tablet {
      display: block;
    }
  }
}
</style>

<style lang="less" scoped>
.in-page-navigation {
  @media print {
    .hidden();
  }

  .in-page-navigation__inner {
    background: @color-fill-normal;
    margin: 0 -@gutter;
    max-width: 100vw;

    @media @tablet {
      height: 100%;
      background: transparent;
      margin: 0;
    }

    nav {
      @media @tablet {
        .sticky();

        top: @desktop-nav-primary-height + @gutter-xl;
        padding: 0 0 @gutter-lg;
      }

      > .heading {
        .relative();

        .trigger {
          .text-body();

          appearance: none;
          cursor: pointer;
          padding: @gutter;
          padding-right: @gutter-lg;
          border: 0;
          border-top: @1px solid @color-fill-divide;
          border-bottom: @1px solid @color-fill-divide;
          width: 100%;
          height: @sticky-nav-height;
          background: transparent;

          @media @tablet {
            padding: 0 @gutter-sm @gutter;
            border: 0;
          }
        }
      }

      // popover
      ul {
        .list-reset();
        .hidden();
        .shadow-popover();

        background: @color-fill-normal;

        @media @tablet {
          .block();

          box-shadow: none;
          background: transparent;
        }

        &.active {
          .block();
        }

        li {
          a {
            .font-sm();
            .font-medium();

            padding: @gutter-xxs @gutter @gutter-xxs;
            border-bottom: @1px solid @color-fill-divide;

            @media @tablet {
              .font-base();

              padding: @gutter-xxs @gutter-sm @gutter-xs;
            }

            span {
              padding-right: @gutter-lg;
            }

            &:link,
            &:visited {
              .text-normal();

              .icon {
                color: @color-primary;
              }
            }

            &.active {
              .text-primary();
            }

            > * {
              pointer-events: none;
            }
          }
        }
      }
    }
  }
}
</style>
